import { useHistory, useParams } from "react-router";
import { IonCol, IonIcon, IonRow } from "@ionic/react";
import toast from "react-hot-toast";
import { useState } from "react";
import dayjs from "dayjs";

import ChatIcon from "../../../../components/ChatIcon";
import { AppButton, Breadcrumbs } from "../../../../components";

import { useMutation, useQuery } from "@apollo/client";
import { GET_QUOTE_BY_ID } from "../../../../graphql/queries/quote.query";
import { UPDATE_ESTIMATE_AS_READ } from "../../../../graphql/mutations/quote.mutation";

import "./details.style.css";
import {
  getNameFromUrl,
  getSignedUrl,
} from "../../../../lib/utils/common.utils";
import { chevronBackOutline } from "ionicons/icons";

export default function Details() {
  const history: any = useHistory();

  const [updateEstimateStatus] = useMutation(UPDATE_ESTIMATE_AS_READ);

  const historySearch = history?.location?.search;
  const urlParams = new URLSearchParams(historySearch);
  const uChat = urlParams.get("chat");

  const { id } = useParams<any>();

  const [quote, setQuote] = useState<any>({});
  const [userDocuments, setUserDocuments] = useState<string[]>([]);
  const [adminDocuments, setAdminDocuments] = useState<string[]>([]);

  const [isChatActive, setIsChatActive] = useState(false);

  useQuery(GET_QUOTE_BY_ID, {
    variables: {
      id: +id,
    },
    onCompleted: async (data) => {
      if (data.getRequestedEstimate.errors) {
        toast.error(data.getRequestedEstimate.errors[0].message);
      } else {
        const dataItem = data.getRequestedEstimate.data;
        setQuote(dataItem);

        // Marking the quote as read, if it is unread
        if (dataItem.status === "unread") {
          updateEstimateStatus({
            variables: {
              id: dataItem.id,
            },
          });
        }

        const userDocs = dataItem?.documents;
        // getting signed url for user documents
        if (Array.isArray(userDocs)) {
          const signedDocs: string[] = [];
          for (const unsignedDoc of userDocs) {
            const signedUrl = await getSignedUrl(unsignedDoc);
            signedDocs.push(signedUrl);
          }
          setUserDocuments(signedDocs);
        }

        const adminDocs = dataItem?.estimateResponse?.documents;
        // getting signed url for admin documents
        if (Array.isArray(adminDocs)) {
          const signedDocs: string[] = [];
          for (const unsignedDoc of adminDocs) {
            const signedUrl = await getSignedUrl(unsignedDoc);
            signedDocs.push(signedUrl);
          }
          setAdminDocuments(signedDocs);
        }

        if (uChat === "true") setIsChatActive(true);
      }
    },
  });

  const breadcrumbEntries = [
    { title: "Area personale", link: "/personale" },
    { title: "Preventivi", link: "/personale/preventivi" },
    { title: "Dettaglio preventivo" },
  ];

  return (
    <div className="app-container">
      <Breadcrumbs items={breadcrumbEntries} />

      <AppButton
        buttonType="back"
        onClick={() => history.goBack()}
        className="app-back-button"
        style={{
          width: 48,
          height: 24,
        }}
      >
        <IonIcon icon={chevronBackOutline} />
      </AppButton>

      <div className="container-with-space">
        <div className="order-detail-container">
          <div className="order-detail-header">
            <div className="order-detail-title">
              <span>Numero preventivo: </span>
              <span className="order-detail-number">{quote?.id}</span>
            </div>
            <div>
              <div className="flex">
                <div>
                  <ChatIcon
                    roomId={quote.roomId}
                    id={quote.id}
                    category="estimate"
                    isModalActive={isChatActive}
                    setIsModalActive={setIsChatActive}
                    orderNumber={""}
                  />
                </div>
                <div className="order-expiry-date">
                  <span>Scadenza preventivo:</span>
                  <span className="order-expires-at">
                    {quote.estimateResponse?.expiresAt ? (
                      <span>
                        {dayjs(quote.estimateResponse?.expiresAt).format(
                          "DD/MM/YYYY"
                        )}
                      </span>
                    ) : (
                      <span>nessuna scadenza</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="order-detail-customer">
            <IonRow>
              <IonCol size="12" sizeMd="6" className="ion-no-padding">
                <p className="order-detail-title">
                  Dettaglio richiesta cliente:
                </p>
                <span>{quote.description} </span>
              </IonCol>
              <IonCol size="12" sizeMd="6" className="ion-no-padding">
                <p className="order-detail-title">Allegati caricati:</p>
                <PreventiviDetailDocument documents={userDocuments} />
              </IonCol>
            </IonRow>
          </div>

          {quote.status !== "notVerified" && (
            <div className="order-detail-cos">
              <IonRow>
                <IonCol size="12" sizeMd="6" className="ion-no-padding">
                  <p className="order-detail-title">Note aggiuntive di COS:</p>
                  <span>{quote.estimateResponse?.description}</span>
                </IonCol>
                <IonCol size="12" sizeMd="6" className="ion-no-padding">
                  <p className="order-detail-title">
                    Allegati caricati da COS:
                  </p>
                  <PreventiviDetailDocument documents={adminDocuments} />
                </IonCol>
              </IonRow>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface IPDProps {
  documents: string[];
}
function PreventiviDetailDocument(props: IPDProps) {
  if (props.documents.length < 1) return <span>Nessuno</span>;

  return (
    <div className="order-detail-container">
      {props.documents.map((doc: string, index: number) => (
        <div key={index} className="order-detail-document">
          <a href={doc} target="_blank" rel="noopener noreferrer">
            <span>{(index + 1).toString().padStart(2, "00")}</span>
            <span> - {getNameFromUrl(doc)}</span>
          </a>
        </div>
      ))}
    </div>
  );
}
